import { useMutation, useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import { Axios } from '@utils/api/axios-instance.ts';
import { POST_URL } from '@utils/api/endpoints.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { WaveInfoStatus } from '@utils/types/editors/sessions.ts';

/**
 * Wraps POST /session/{id}/regenerate_wave_info. The backend increments
 * the wave_info_version and returns the initial status; the editor should
 * then poll GET /session/{id} to catch the pending → running → done|failed
 * transition and refresh the waveform when it lands.
 */
export type RegenerateWaveInfoResult = {
  status: WaveInfoStatus;
  wave_info_version: number;
  error: string | null;
};

async function regenerateWaveInfo(
  sessionId: string
): Promise<RegenerateWaveInfoResult> {
  const { data } = await Axios.post<RegenerateWaveInfoResult>(
    POST_URL(sessionId).regenerateWaveInfo
  );
  return data;
}

export function useRegenerateWaveInfo(sessionId?: string) {
  const queryClient = useQueryClient();
  return useMutation<RegenerateWaveInfoResult, AxiosError<BaseError>>(
    () => {
      if (!sessionId) {
        return Promise.reject(new Error('sessionId is required'));
      }
      return regenerateWaveInfo(sessionId);
    },
    {
      onSuccess: () => {
        if (sessionId) {
          queryClient.invalidateQueries([
            'transcript-editor-session',
            sessionId
          ]);
        }
      }
    }
  );
}
